import { ColDef, ICellRendererParams } from 'ag-grid-enterprise';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { activityStateEnum, permissionLevelEnum, systemConfigKeyEnum } from '../../../../constants';
import { getActivityStateColor } from '../../../../constants/colors';
import { getActivityStateLabel } from '../../../../constants/labels';
import { useValidateSiteActivitiesMutation } from '../../../../hooks/mutations/useActivityMutation';
import { useAuth } from '../../../../hooks/useAuth';
import { useBoolean } from '../../../../hooks/useBoolean';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { useTheme } from '../../../../hooks/useTheme';
import { ValidationSiteSummary } from '../../../../services/apiSLTP/dailyReports/activities';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { formatDateToYYYYMMDD } from '../../../../utils/dates';
import { formatNumber } from '../../../../utils/numbers';
import StateCell from '../../../cells/StateCell';
import ControlValidationItemsModal from '../../../modals/operations/validation/ControlValidationItemsModal';
import IconButton from '../../../ui/IconButton';
import IconLine from '../../../ui/IconLine';
import GenericAgGridTable from '../../GenericAgGridTable';

interface SitesValidationSummaryTableProps {
    data: ValidationSiteSummary[];
    isLoading: boolean;
    currentDate: Date;
    refetch: () => void;
}

const SitesValidationSummaryTable: React.FC<SitesValidationSummaryTableProps> = ({ data, isLoading, currentDate, refetch }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { getConfigValue } = useSystemConfig();
    const { user } = useAuth();
    const { muiTheme } = useTheme();
    const validationControlModal = useBoolean(false);

    const { mutate: validateSite, isLoading: isValidateSiteLoading } = useValidateSiteActivitiesMutation({ onSuccess: () => refetch() });

    const handleOpen = (validationSite: ValidationSiteSummary) => navigate(`/operations/validation/site/${validationSite.site._id}`);

    const columnDefs: ColDef<ValidationSiteSummary>[] = useMemo(
        () =>
            [
                {
                    headerName: capitalizeFirstLetter(t('conduc')),
                    valueGetter: (params) => {
                        return params.data?.site.conducId?.username || null;
                    },
                    cellRenderer: (params: ICellRendererParams<ValidationSiteSummary>) => {
                        if (!params.data) return params.value;
                        if (!params.data?.site.conducId) return null;
                        return (
                            <StateCell
                                stateColor={params.data?.site.conducId?.color || ''}
                                text={params.data?.site.conducId?.username || ''}
                                showDot={false}
                                style={{ width: 60, justifyContent: 'center' }}
                            />
                        );
                    },
                    width: 150,
                    colId: 'conduc',
                },
                {
                    headerName: capitalizeFirstLetter(t('site')),
                    field: 'site.name',
                    colId: 'site',
                    initialFlex: 1,
                    minWidth: 300,
                },
                {
                    headerName: capitalizeFirstLetter(t('actions')),
                    colId: 'actions',
                    sortable: false,
                    filter: false,
                    initialWidth: 150,
                    cellRenderer: (params: ICellRendererParams<ValidationSiteSummary>) => {
                        if (!params.data) return null;
                        const validationSite = params.data;
                        return (
                            <IconLine>
                                <IconButton iconName="fileOpen" title={capitalizeFirstLetter(t('open'))} onClick={() => handleOpen(validationSite)} />
                                {(user?.profileId.permsLevel ?? Infinity) <= permissionLevelEnum.SUPERVISION &&
                                    [activityStateEnum.SEMIVALIDATED, activityStateEnum.VALIDATED].includes(params.data.siteState) && (
                                        <IconButton
                                            iconName="lockOpen"
                                            title={capitalizeFirstLetter(t('unlock'))}
                                            onClick={() =>
                                                validateSite({
                                                    siteId: validationSite.site._id,
                                                    body: { date: currentDate.toISOString(), activityState: activityStateEnum.VALIDATION },
                                                })
                                            }
                                        />
                                    )}
                                {(user?.profileId.permsLevel ?? Infinity) <= permissionLevelEnum.SUPERVISION &&
                                    [activityStateEnum.ENTERED].includes(params.data.siteState) && (
                                        <IconButton
                                            iconName="lockOpen"
                                            title={capitalizeFirstLetter(t('unlock'))}
                                            onClick={() =>
                                                validateSite({
                                                    siteId: validationSite.site._id,
                                                    body: { date: currentDate.toISOString(), activityState: activityStateEnum.VALIDATED },
                                                })
                                            }
                                        />
                                    )}
                            </IconLine>
                        );
                    },
                },
                {
                    headerName: capitalizeFirstLetter(t('status')),
                    valueGetter: (params) => capitalizeFirstLetter(getActivityStateLabel(params.data?.siteState)),
                    cellRenderer: (params: ICellRendererParams<ValidationSiteSummary>) => {
                        if (!params.data) return params.value;
                        return (
                            <StateCell
                                stateColor={getActivityStateColor(params.data?.siteState)}
                                text={capitalizeFirstLetter(getActivityStateLabel(params.data?.siteState))}
                            />
                        );
                    },
                    colId: 'status',
                },
                {
                    headerName: `${capitalizeFirstLetter(t('price'))} (${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)})`,
                    field: 'totalDailyPrice',
                    valueFormatter: (params) => (params.value !== undefined ? formatNumber(params.value, t('locale'), 2) : '-'),
                    cellStyle: (params) => ({ color: params.value > 0 ? muiTheme.palette.success.main : muiTheme.palette.text.disabled }),
                    colId: 'totalDailyPrice',
                    type: 'numericColumn',
                    aggFunc: 'sum',
                    filter: 'agNumberColumnFilter',
                },
                {
                    headerName: capitalizeFirstLetter(t('codeProC')),
                    field: 'site.codeProC',
                    colId: 'codeProC',
                },
                {
                    headerName: capitalizeFirstLetter(t('nbActivities')),
                    field: 'nbActivities',
                    colId: 'nbActivities',
                    type: 'numericColumn',
                    filter: 'agNumberColumnFilter',
                },
                {
                    headerName: capitalizeFirstLetter(t('nbValidatedActivities')),
                    field: 'nbValidatedActivities',
                    colId: 'nbValidatedActivities',
                    type: 'numericColumn',
                    filter: 'agNumberColumnFilter',
                },
                {
                    headerName: capitalizeFirstLetter(t('nbInValidationActivities')),
                    field: 'nbInValidationActivities',
                    colId: 'nbInValidationActivities',
                    type: 'numericColumn',
                    filter: 'agNumberColumnFilter',
                },
                {
                    headerName: capitalizeFirstLetter(t('nbMissingActivities')),
                    field: 'nbMissingActivities',
                    colId: 'nbMissingActivities',
                    type: 'numericColumn',
                    filter: 'agNumberColumnFilter',
                },
            ] as ColDef<ValidationSiteSummary>[],
        [t, user, currentDate, muiTheme]
    );

    return (
        <>
            <GenericAgGridTable
                rowData={data.sort((a, b) => {
                    if (
                        formatName(a.site.conducId?.firstName, a.site.conducId?.lastName) <
                        formatName(b.site.conducId?.firstName, b.site.conducId?.lastName)
                    )
                        return -1;
                    if (
                        formatName(a.site.conducId?.firstName, a.site.conducId?.lastName) >
                        formatName(b.site.conducId?.firstName, b.site.conducId?.lastName)
                    )
                        return 1;
                    const siteA = a.site.name.toLowerCase();
                    const siteB = b.site.name.toLowerCase();
                    if (siteA < siteB) return -1;
                    if (siteA > siteB) return 1;
                    return 0;
                })}
                columnDefs={columnDefs}
                loading={isLoading}
                exportCsvFileName={'sites-validation-' + formatDateToYYYYMMDD(currentDate)}
                agGridProps={{
                    getRowId: (params) => params.data.site._id,
                    grandTotalRow: 'pinnedTop',
                }}
                headerRightComponent={
                    <>
                        <IconButton iconName="factCheck" onClick={validationControlModal.setTrue} />
                    </>
                }
            />
            {validationControlModal.value && (
                <ControlValidationItemsModal
                    open={validationControlModal.value}
                    handleClose={validationControlModal.setFalse}
                    date={currentDate.toISOString()}
                />
            )}
        </>
    );
};

export default SitesValidationSummaryTable;
